import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import DailyReportDownloader from './Reports';
import Header from '../Components/Header';

const DownloadWeatherData = () => {
  const [devices, setDevices] = useState([]); // Initialize as empty array
  const [selectedDevice, setSelectedDevice] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('00:00:00');
  const [endTime, setEndTime] = useState('23:59:59');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      setError(''); // Clear previous errors
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found. Please login again.');
        setLoading(false);
        return;
      }
      
      const response = await axios.get('/api/user/devices', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      // Check if response is actually JSON
      if (typeof response.data === 'string' && response.data.includes('<!doctype')) {
        throw new Error('Server returned HTML instead of JSON. API might be down or misconfigured.');
      }
      
      // Safely check if devices exist and is an array
      if (response.data && Array.isArray(response.data.devices)) {
        setDevices(response.data.devices);
        
        // Set first device as default if available
        if (response.data.devices.length > 0) {
          const firstDevice = typeof response.data.devices[0] === 'string' 
            ? response.data.devices[0] 
            : response.data.devices[0].device_id;
          setSelectedDevice(firstDevice);
        }
      } else {
        console.warn('Unexpected API response structure:', response.data);
        setError('Unexpected response from server. Please contact support.');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching devices:', err);
      setLoading(false);
      
      if (err.response) {
        // Check if the error response is HTML
        if (err.response.headers['content-type']?.includes('text/html')) {
          setError('Server error: API returned HTML instead of JSON. Please contact support.');
        } else if (err.response.status === 401) {
          setError('Authentication failed. Please login again.');
        } else if (err.response.status === 503) {
          setError('Service temporarily unavailable. Please try again later.');
        } else {
          setError(`Server error: ${err.response.status}. Please try again later.`);
        }
      } else if (err.request) {
        setError('Cannot connect to server. Please check your internet connection.');
      } else {
        setError(`Error: ${err.message}`);
      }
      
      // Show user-friendly popup only for unexpected errors
      if (!err.response || err.response.status >= 500) {
        Swal.fire({
          "icon": "error",
          "title": "Connection Error",
          "text": "Unable to connect to the server. Please check your connection and try again."
        });
      }
    }
  };

  const handleDeviceChange = (e) => {
    setSelectedDevice(e.target.value);
  };

  const formatTime = (timeStr) => {
    if (timeStr.length === 5) {
      return `${timeStr}:00`;
    }
    return timeStr;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDevice || !startDate || !endDate) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found. Please login again.');
        setLoading(false);
        return;
      }
      
      const formattedStartTime = formatTime(startTime);
      const formattedEndTime = formatTime(endTime);
      
      const url = `/api/download-data?start_date=${startDate}&end_date=${endDate}&start_time=${formattedStartTime}&end_time=${formattedEndTime}&device_id=${selectedDevice}`;
      
      const response = await axios({
        url,
        method: 'GET',
        responseType: 'blob', 
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/csv,application/octet-stream'
        }
      });

      const contentType = response.headers['content-type'];
      if (contentType && contentType.indexOf('application/json') !== -1) {
        const reader = new FileReader();
        reader.onload = () => {
          const errorJson = JSON.parse(reader.result);
          setError(`Error: ${errorJson.detail || 'Unknown error'}`);
          setLoading(false);
        };
        reader.readAsText(response.data);
        return;
      }

      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      const timeInfo = `${startDate}T${formattedStartTime}_to_${endDate}T${formattedEndTime}`;
      link.setAttribute('download', `weather_data_${selectedDevice}_${timeInfo}.csv`);
      
      document.body.appendChild(link);
      link.click();
      
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(link);
      
      setSuccess('Download started successfully');
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error('Error downloading data:', err);
      
      if (err.response) {
        if (err.response.data instanceof Blob) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorJson = JSON.parse(reader.result);
              setError(`Error: ${errorJson.detail || 'Unknown error'}`);
            } catch (e) {
              setError(`Error: ${err.response.status} - Could not parse error details`);
            }
          };
          reader.readAsText(err.response.data);
        } else {
          setError(`Error: ${err.response.status} - ${err.response.data.detail || 'Unknown error'}`);
        }
      } else if (err.request) {
        setError('No response from server. Please check your connection.');
      } else {
        setError(`Error: ${err.message}`);
      }
    }
  };

  const getDeviceDisplayName = (device) => {
    if (typeof device === 'string') {
      return device;
    }
    return `${device.device_id} (${device.username || 'Unknown'})`;
  };

  return (
    <>
      <Header/>
      
      <div className="p-6 mt-12 max-w-md mx-auto bg-white rounded-lg shadow-md">
        <h1 className="text-2xl font-bold mb-6 text-center">Download Data</h1>
        
        {loading && <p className="text-center text-blue-500">Loading...</p>}
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
            <button 
              onClick={fetchDevices} 
              className="ml-2 text-sm underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        )}
        
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="device" className="block text-sm font-medium text-gray-700 mb-1">
              Select Device
            </label>
            <select
              id="device"
              value={selectedDevice}
              onChange={handleDeviceChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
              disabled={loading || devices.length === 0}
            >
              <option value="">
                {devices.length === 0 ? 'No devices available' : 'Select a device'}
              </option>
              {Array.isArray(devices) && devices.map((device, index) => {
                const deviceId = typeof device === 'string' ? device : device.device_id;
                return (
                  <option key={index} value={deviceId}>
                    {getDeviceDisplayName(device)}
                  </option>
                );
              })}
            </select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            
            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="startTime" className="block text-sm font-medium text-gray-700 mb-1">
                Start Time
              </label>
              <input
                type="time"
                id="startTime"
                value={startTime}
                step="1"
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Default: 00:00:00</p>
            </div>
            
            <div>
              <label htmlFor="endTime" className="block text-sm font-medium text-gray-700 mb-1">
                End Time
              </label>
              <input
                type="time"
                id="endTime"
                value={endTime}
                step="1"
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Default: 23:59:59</p>
            </div>
          </div>
          
          {startDate && endDate && (
            <div className="mt-2">
              <p className="text-sm text-gray-600 italic">
                Data will be filtered from {startDate} {startTime} to {endDate} {endTime}
              </p>
            </div>
          )}
          
          <button
            type="submit"
            disabled={loading || devices.length === 0}
            className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
          >
            {loading ? 'Processing...' : 'Download Data'}
          </button>
        </form>
      </div>
      
      <div className='mt-20'>
        <DailyReportDownloader/>
      </div>
    </>
  );
};

export default DownloadWeatherData;
